// Set once useCurrentUser() (see AuthContext.tsx) first confirms a real session, cleared on
// explicit logout (see useLogout.ts) — persisted in localStorage (not just in-memory JS state)
// so it survives a tab close/reopen. forceLogout() below reads this to tell "this browser was
// genuinely logged in before, and that session just expired" (show the message) apart from
// "this is the first-ever, expected 401 from a visitor who was never logged in at all" (stay
// silent) — from inside a single 401-then-failed-refresh sequence, those two cases are
// otherwise indistinguishable.
export const HAS_AUTHENTICATED_KEY = 'pulse.hasAuthenticated';

// Every domain module's api.ts should use this instead of raw fetch() — httpOnly auth
// cookies are not attached to fetch() calls without credentials: 'include'.
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(path, { ...init, credentials: 'include' });
  if (res.status !== 401 || path === REFRESH_PATH) {
    return res;
  }

  // Try to refresh the session when the access token has expired, and log the user out if the
  // session could not be refreshed.
  const refreshed = await refreshSession();
  if (!refreshed) {
    forceLogout();
    return res;
  }

  // Retry the original request after a successful refresh.
  return fetch(path, { ...init, credentials: 'include' });
}

const REFRESH_PATH = '/api/v1/auth/refresh';

// If multiple API requests fail with 401 at the same time,
// send only one refresh request to the server.
// All other requests wait for the same refresh request to finish
// instead of creating duplicate refresh requests.
let refreshPromise: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = fetch(REFRESH_PATH, { method: 'POST', credentials: 'include' })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// If the refresh request also returns 401, the user's session has expired.
// Log the user out and redirect to the login page.
// Prevent multiple logout attempts if several API requests fail together.
let forcingLogout = false;

function forceLogout() {
  if (forcingLogout || window.location.pathname === '/login') return;
  forcingLogout = true;

  const wasAuthenticated = localStorage.getItem(HAS_AUTHENTICATED_KEY) === '1';
  if (!wasAuthenticated) {
    // Never actually had a session on this browser (e.g. the very first /auth/me check for a
    // brand-new visitor) — a 401 here is the normal, expected state, not an error to report.
    // No /auth/logout call either: there is nothing to log out of.
    window.location.href = '/login';
    return;
  }

  fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
  localStorage.removeItem(HAS_AUTHENTICATED_KEY);
  window.location.href = '/login?error=session_expired';
}
